import { useReducer, useCallback, useEffect, useRef } from 'react';

/**
 * Modal state as discriminated union
 * Makes impossible states impossible (e.g., can't have confirm and edit open at same time)
 */
export type ModalState =
    | { type: 'closed' }
    | { type: 'menu' }
    | { type: 'confirm-delete'; isDeleting: boolean }
    | { type: 'edit' };

/**
 * All possible actions for the expense card state machine
 */
type ModalAction =
    | { type: 'OPEN_MENU' }
    | { type: 'OPEN_CONFIRM_DELETE' }
    | { type: 'OPEN_EDIT' }
    | { type: 'START_DELETING' }
    | { type: 'CLOSE' };

/**
 * State machine reducer for expense card modals
 * Enforces valid state transitions and prevents impossible states
 */
function modalReducer(state: ModalState, action: ModalAction): ModalState {
    switch (action.type) {
        case 'OPEN_MENU':
            return { type: 'menu' };

        case 'OPEN_CONFIRM_DELETE':
            // Can only open confirm from menu or closed state
            if (state.type === 'menu' || state.type === 'closed') {
                return { type: 'confirm-delete', isDeleting: false };
            }
            return state;

        case 'OPEN_EDIT':
            // Can only open edit from menu or closed state
            if (state.type === 'menu' || state.type === 'closed') {
                return { type: 'edit' };
            }
            return state;

        case 'START_DELETING':
            // Can only start deleting if in confirm-delete state
            if (state.type === 'confirm-delete') {
                return { type: 'confirm-delete', isDeleting: true };
            }
            return state;

        case 'CLOSE':
            return { type: 'closed' };

        default:
            return state;
    }
}

/**
 * Custom hook for expense card state management
 * 
 * Manages modal states using a state machine pattern with discriminated unions.
 * Prevents impossible states (e.g., multiple modals open simultaneously).
 * 
 * @returns State, action dispatchers, and menu ref for click-outside detection
 * 
 * @example
 * ```tsx
 * const { state, actions, menuRef } = useExpenseCardState();
 * 
 * // Check current state
 * if (state.type === 'menu') {
 *   // Show menu
 * }
 * 
 * // Transition states
 * actions.openMenu();
 * actions.openConfirmDelete();
 * actions.close();
 * ```
 */
export function useExpenseCardState() {
    const [state, dispatch] = useReducer(modalReducer, { type: 'closed' });
    const menuRef = useRef<HTMLDivElement>(null);

    // Close menu when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                dispatch({ type: 'CLOSE' });
            }
        }

        if (state.type === 'menu') {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [state.type]);

    // Memoized action creators
    const actions = {
        openMenu: useCallback(() => dispatch({ type: 'OPEN_MENU' }), []),
        openConfirmDelete: useCallback(() => dispatch({ type: 'OPEN_CONFIRM_DELETE' }), []),
        openEdit: useCallback(() => dispatch({ type: 'OPEN_EDIT' }), []),
        startDeleting: useCallback(() => dispatch({ type: 'START_DELETING' }), []),
        close: useCallback(() => dispatch({ type: 'CLOSE' }), []),
    };

    return { state, actions, menuRef };
}
